/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import { getMergedSettings, updateSettings, type MergedSettings } from '../lib/api'

interface SettingsContextType {
  settings: MergedSettings | null
  loading: boolean
  error: string | null
  updateSetting: <K extends keyof MergedSettings>(key: K, value: MergedSettings[K]) => Promise<void>
  updateSettings: (updates: Partial<MergedSettings>) => Promise<void>
  refreshSettings: () => Promise<void>
}

const SettingsContext = createContext<SettingsContextType | null>(null)

const DEFAULT_SETTINGS: MergedSettings = {
  theme: 'dark',
  editor_font_size: 14,
  terminal_font_size: 14,
  vnc_resolution: '1920x1080',
  auto_save: true,
  dashboard: {},
  terminal: {},
  vscode: {},
  browser: {},
  claude_code: {},
  customized_categories: [],
}

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<MergedSettings | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Fetch settings on mount
  useEffect(() => {
    let mounted = true

    async function loadSettings() {
      try {
        const data = await getMergedSettings()
        if (mounted) {
          setSettings(data)
          setError(null)
        }
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err.message : 'Failed to load settings')
          // Use defaults on error
          setSettings(DEFAULT_SETTINGS)
        }
      } finally {
        if (mounted) {
          setLoading(false)
        }
      }
    }

    loadSettings()

    return () => {
      mounted = false
    }
  }, [])

  // Update a single setting
  const updateSetting = useCallback(async <K extends keyof MergedSettings>(
    key: K,
    value: MergedSettings[K]
  ) => {
    if (!settings) return

    // Optimistically update local state
    setSettings((prev) => (prev ? { ...prev, [key]: value } : prev))

    try {
      await updateSettings({ [key]: value })
      // Refresh to get the merged result
      const updated = await getMergedSettings()
      setSettings(updated)
      setError(null)
    } catch (err) {
      // Revert on error
      setSettings(settings)
      setError(err instanceof Error ? err.message : 'Failed to update setting')
      throw err
    }
  }, [settings])

  // Update multiple settings at once
  const updateSettingsBatch = useCallback(async (updates: Partial<MergedSettings>) => {
    if (!settings) return

    // Optimistically update local state
    setSettings((prev) => (prev ? { ...prev, ...updates } : prev))

    try {
      await updateSettings(updates)
      // Refresh to get the merged result
      const updated = await getMergedSettings()
      setSettings(updated)
      setError(null)
    } catch (err) {
      // Revert on error
      setSettings(settings)
      setError(err instanceof Error ? err.message : 'Failed to update settings')
      throw err
    }
  }, [settings])

  // Refresh settings from server
  const refreshSettings = useCallback(async () => {
    try {
      const data = await getMergedSettings()
      setSettings(data)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to refresh settings')
    }
  }, [])

  return (
    <SettingsContext.Provider
      value={{
        settings,
        loading,
        error,
        updateSetting,
        updateSettings: updateSettingsBatch,
        refreshSettings,
      }}
    >
      {children}
    </SettingsContext.Provider>
  )
}

export function useSettings() {
  const ctx = useContext(SettingsContext)
  if (!ctx) {
    throw new Error('useSettings must be used within SettingsProvider')
  }
  return ctx
}

// Convenience hook for accessing theme
export function useTheme() {
  const { settings, updateSetting, loading } = useSettings()

  const setTheme = useCallback(async (theme: string) => {
    await updateSetting('theme', theme)
  }, [updateSetting])

  return {
    theme: settings?.theme ?? 'dark',
    setTheme,
    loading,
  }
}
